import './GuideLauncher.css';

import {
  CSSProperties,
  PointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { flagOutline, listOutline } from 'ionicons/icons';
import { useTranslate } from '../../../hooks/context/useTranslate';
import useRoutes from '../../../hooks/useRoutes';
import useLocalStorageKey from '../../../hooks/useLocalStorageKey';
import { useUserState } from '../../../hooks/context/useUserState';
import { useGuideContext } from '../../../context/GuideContext/GuideContext';
import { getGuideProgress } from '../../../utils/guides/guideProgressStore';
import {
  GuideLauncherPosition,
  guideLauncherPositionStorageKey,
  readGuideLauncherPosition,
  writeGuideLauncherPosition,
} from '../../../utils/guides/guideLauncherPosition';
import { useQuickBookmarks } from '../../../context/QuickBookmarksContext/QuickBookmarksContext';

const LAUNCHER_SIZE = 64;
const SAFE_MARGIN = 12;
const DRAG_THRESHOLD = 5;
const DOCK_STEP = 72;
const REMINDER_WIDTH = 330;

const clampPixels = (value: number, maximum: number): number =>
  Math.min(
    Math.max(SAFE_MARGIN, value),
    Math.max(SAFE_MARGIN, maximum - LAUNCHER_SIZE - SAFE_MARGIN)
  );

export const getGuidePanelRevealRadius = (
  originX: number,
  originY: number,
  panelWidth: number,
  panelHeight: number
): number => {
  const horizontalDistance = Math.max(
    Math.abs(originX),
    Math.abs(panelWidth - originX)
  );
  const verticalDistance = Math.max(
    Math.abs(originY),
    Math.abs(panelHeight - originY)
  );
  return Math.ceil(Math.hypot(horizontalDistance, verticalDistance));
};

// Persistent layer-A entry point: draggable per user, contextual on agenda screens.
const GuideLauncher = () => {
  const { t } = useTranslate();
  const history = useHistory();
  const location = useLocation();
  const routes = useRoutes();
  const localStorageKey = useLocalStorageKey();
  const {
    state: { userLogin },
  } = useUserState();
  const {
    index,
    ensureIndex,
    progress,
    activeTour,
    currentScreenGuideId,
    openPanel,
  } = useGuideContext();
  const {
    items: quickBookmarks,
    view: quickBookmarksView,
    openCreate,
    openList,
    close: closeQuickBookmarks,
  } = useQuickBookmarks();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    dragged: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const storageKey = guideLauncherPositionStorageKey(localStorageKey);
  const [normalized, setNormalized] = useState<GuideLauncherPosition>(() =>
    readGuideLauncherPosition(storageKey, userLogin)
  );
  const [pixels, setPixels] = useState({ left: 0, top: 0 });
  const pixelsRef = useRef(pixels);

  const updatePixels = useCallback((left: number, top: number) => {
    const next = { left, top };
    pixelsRef.current = next;
    setPixels(next);
  }, []);

  useEffect(() => {
    ensureIndex();
  }, [ensureIndex]);

  useEffect(() => {
    setNormalized(readGuideLauncherPosition(storageKey, userLogin));
  }, [storageKey, userLogin]);

  const updateOrigin = useCallback((left: number, top: number) => {
    const outlet = buttonRef.current?.parentElement;
    if (!outlet) return;
    const panelWidth = Math.min(400, outlet.clientWidth);
    const originX =
      left + LAUNCHER_SIZE / 2 - (outlet.clientWidth - panelWidth);
    const originY = top + LAUNCHER_SIZE / 2;
    outlet.style.setProperty('--guide-origin-x', `${originX}px`);
    outlet.style.setProperty('--guide-origin-y', `${originY}px`);
    outlet.style.setProperty(
      '--guide-reveal-radius',
      `${getGuidePanelRevealRadius(
        originX,
        originY,
        panelWidth,
        outlet.clientHeight
      )}px`
    );
    const reminderLeft =
      left + LAUNCHER_SIZE + SAFE_MARGIN + REMINDER_WIDTH <= outlet.clientWidth
        ? left + LAUNCHER_SIZE + SAFE_MARGIN
        : Math.max(SAFE_MARGIN, left - REMINDER_WIDTH - SAFE_MARGIN);
    outlet.style.setProperty('--quick-reminder-left', `${reminderLeft}px`);
    outlet.style.setProperty('--quick-reminder-top', `${top}px`);
  }, []);

  const positionFromNormalized = useCallback(
    (position: GuideLauncherPosition) => {
      const outlet = buttonRef.current?.parentElement;
      if (!outlet) return;
      const centreX = position.x * outlet.clientWidth;
      const centreY = position.y * outlet.clientHeight;
      const left = clampPixels(centreX - LAUNCHER_SIZE / 2, outlet.clientWidth);
      const top = Math.max(
        SAFE_MARGIN + DOCK_STEP * 2,
        clampPixels(centreY - LAUNCHER_SIZE / 2, outlet.clientHeight)
      );
      updatePixels(left, top);
      updateOrigin(left, top);
    },
    [updateOrigin, updatePixels]
  );

  useLayoutEffect(() => {
    positionFromNormalized(normalized);
    const outlet = buttonRef.current?.parentElement;
    if (!outlet || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() =>
      positionFromNormalized(normalized)
    );
    observer.observe(outlet);
    return () => observer.disconnect();
  }, [normalized, positionFromNormalized]);

  const persistPixels = useCallback(
    (left: number, top: number) => {
      const outlet = buttonRef.current?.parentElement;
      if (!outlet) return;
      const position = {
        x: (left + LAUNCHER_SIZE / 2) / Math.max(1, outlet.clientWidth),
        y: (top + LAUNCHER_SIZE / 2) / Math.max(1, outlet.clientHeight),
      };
      setNormalized(position);
      writeGuideLauncherPosition(storageKey, userLogin, position);
    },
    [storageKey, userLogin]
  );

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      dragged: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const outlet = buttonRef.current?.parentElement;
    if (!drag || drag.pointerId !== event.pointerId || !outlet) return;
    if (
      Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) >=
      DRAG_THRESHOLD
    ) {
      drag.dragged = true;
    }
    if (!drag.dragged) return;
    const bounds = outlet.getBoundingClientRect();
    const left = clampPixels(
      event.clientX - bounds.left - drag.offsetX,
      bounds.width
    );
    const top = Math.max(
      SAFE_MARGIN + DOCK_STEP * 2,
      clampPixels(event.clientY - bounds.top - drag.offsetY, bounds.height)
    );
    updatePixels(left, top);
    updateOrigin(left, top);
  };

  const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    if (drag.dragged) {
      suppressClickRef.current = true;
      persistPixels(pixelsRef.current.left, pixelsRef.current.top);
    }
  };

  const moveWithKeyboard = (deltaX: number, deltaY: number) => {
    const outlet = buttonRef.current?.parentElement;
    if (!outlet) return;
    const left = clampPixels(pixels.left + deltaX, outlet.clientWidth);
    const top = Math.max(
      SAFE_MARGIN + DOCK_STEP * 2,
      clampPixels(pixels.top + deltaY, outlet.clientHeight)
    );
    updatePixels(left, top);
    updateOrigin(left, top);
    persistPixels(left, top);
  };

  const path = index?.paths[0];
  const startBannerVisible =
    !!path && !getGuideProgress(progress, 'path', path.id);
  const onGuideHub = location.pathname === routes.guides();
  const hidden = !index || !!activeTour || startBannerVisible || onGuideHub;

  const style = { left: pixels.left, top: pixels.top } as CSSProperties;
  const listStyle = {
    left: pixels.left,
    top: pixels.top - DOCK_STEP,
  } as CSSProperties;
  const flagStyle = {
    left: pixels.left,
    top: pixels.top - DOCK_STEP * 2,
  } as CSSProperties;

  return (
    <>
      <button
        type="button"
        className={`guide-launcher quick-bookmark-launcher quick-bookmark-flag${hidden ? ' guide-launcher-hidden' : ''}${quickBookmarksView === 'create' ? ' is-active' : ''}`}
        style={flagStyle}
        onClick={openCreate}
        title={t('quickBookmarks.new')}
        aria-label={t('quickBookmarks.new')}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        data-quick-bookmark-create
      >
        <IonIcon icon={flagOutline} />
      </button>
      <button
        type="button"
        className={`guide-launcher quick-bookmark-launcher quick-bookmark-list${hidden ? ' guide-launcher-hidden' : ''}${quickBookmarksView === 'list' ? ' is-active' : ''}`}
        style={listStyle}
        onClick={openList}
        title={t('quickBookmarks.list')}
        aria-label={t('quickBookmarks.list')}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        data-quick-bookmark-list
      >
        <IonIcon icon={listOutline} />
        {quickBookmarks.length > 0 && (
          <span className="quick-bookmark-count">{quickBookmarks.length}</span>
        )}
      </button>
      <button
        ref={buttonRef}
        type="button"
        className={`guide-launcher${hidden ? ' guide-launcher-hidden' : ''}`}
        style={style}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 24 : 8;
          if (event.key === 'ArrowLeft') moveWithKeyboard(-step, 0);
          else if (event.key === 'ArrowRight') moveWithKeyboard(step, 0);
          else if (event.key === 'ArrowUp') moveWithKeyboard(0, -step);
          else if (event.key === 'ArrowDown') moveWithKeyboard(0, step);
          else return;
          event.preventDefault();
        }}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          closeQuickBookmarks();
          updateOrigin(pixels.left, pixels.top);
          if (currentScreenGuideId) openPanel(currentScreenGuideId);
          else history.push(routes.guides());
        }}
        title={t('guide.launcherHint')}
        aria-label={t('guide.title')}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        data-guide-launcher
      >
        <span aria-hidden="true">?</span>
      </button>
    </>
  );
};

export default GuideLauncher;
